import { Injectable, inject } from '@angular/core';
import { ICliente } from 'modelos/src';
import { ClienteSelectorCacheService } from '../../componentes/selectores/cliente-selector/cliente-selector-cache.service';
import { LoginService } from '../../modulos/login/login.service';
import { escaparHtml } from '../html-seguro.util';

/**
 * Resuelve la cadena de clientes ancestros (del cliente del usuario logueado
 * hasta el cliente en cuestión) para mostrarla en las tablas: en el listado
 * sólo se ve el nombre y con clientes homónimos o parecidos no se distingue de
 * quién cuelga cada uno.
 *
 * Pensado para usarse en las columnas de `app-tabla`, que renderizan strings:
 *
 * ```ts
 * {
 *   header: { label: 'Cliente' },
 *   row: {
 *     html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
 *     tooltip: (dato) => this.clienteJerarquia.tooltipJerarquia(dato.cliente),
 *   },
 * }
 * ```
 *
 * Los métodos son sincrónicos porque la tabla los invoca en cada change
 * detection: se apoyan en el índice ya cargado y, si todavía no está, muestran
 * sólo el nombre y se re-renderizan solos cuando llegan los clientes.
 */
@Injectable({ providedIn: 'root' })
export class ClienteJerarquiaService {
  private cacheClientes = inject(ClienteSelectorCacheService);

  private porId = new Map<string, ICliente>();
  private timestamp: number | null = null;
  private cargando = false;
  private readonly DURACION_INDICE = 5 * 60 * 1000;

  /// Clientes visibles indexados por _id. El cache hace deep copy en cada
  /// lectura, así que se indexa una vez y no por celda.
  private asegurarIndice(): void {
    const vigente =
      this.timestamp !== null &&
      Date.now() - this.timestamp < this.DURACION_INDICE;
    if (vigente || this.cargando) return;

    this.cargando = true;
    this.cacheClientes.getClientes().subscribe({
      next: (clientes) => {
        this.porId = new Map(clientes.map((c) => [c._id!, c]));
        this.timestamp = Date.now();
        this.cargando = false;
      },
      error: (error) => {
        console.error('Error cargando clientes para la jerarquía:', error);
        this.cargando = false;
      },
    });
  }

  /**
   * Cadena de clientes desde el ancestro más alto visible (normalmente el
   * cliente del usuario logueado) hasta el cliente recibido, incluido.
   * Devuelve sólo al cliente si todavía no hay índice o no se puede subir.
   */
  public cadena(cliente?: ICliente | string | null): ICliente[] {
    this.asegurarIndice();

    const id = typeof cliente === 'string' ? cliente : cliente?._id;
    if (!id) return [];

    const propio = LoginService.getCliente();
    // El objeto de la fila puede venir populado con datos que el índice no
    // tiene todavía: sirve como fallback para no quedarse sin nombre.
    const inicial =
      this.porId.get(id) ?? (typeof cliente === 'object' ? cliente : undefined);
    if (!inicial) return [];

    const cadena: ICliente[] = [];
    const vistos = new Set<string>();
    let actual: ICliente | undefined = inicial;

    while (actual && !vistos.has(actual._id!)) {
      vistos.add(actual._id!);
      cadena.unshift(actual);
      if (actual._id === propio?._id) break;
      actual = actual.idPadre ? this.porId.get(actual.idPadre) : undefined;
    }

    return cadena;
  }

  /**
   * Tooltip con la jerarquía, un cliente por línea y flechas entre niveles.
   * Devuelve undefined si no hay ancestros que mostrar: repetir el nombre que
   * ya se ve en la celda no aporta nada.
   */
  public tooltipJerarquia(
    cliente?: ICliente | string | null,
  ): string | undefined {
    const cadena = this.cadena(cliente);
    if (cadena.length < 2) return undefined;

    return cadena
      .map((c, i) => {
        // El nombre lo carga un usuario: escapado (XSS almacenado).
        const nombre = escaparHtml(c.nombre || 'Sin nombre');
        const linea =
          i === cadena.length - 1
            ? `<div><strong>${nombre}</strong></div>`
            : `<div>${nombre}</div>`;
        const flecha = i < cadena.length - 1 ? `<div>&nbsp;&nbsp;⬇</div>` : '';
        return linea + flecha;
      })
      .join('');
  }

  /**
   * Nombre del cliente con un icono que avisa que hay jerarquía detrás (y por
   * lo tanto tooltip). Para usar en `row.html`.
   */
  public htmlNombre(cliente?: ICliente | string | null): string {
    const cadena = this.cadena(cliente);
    const crudo =
      cadena[cadena.length - 1]?.nombre ||
      (typeof cliente === 'object' ? cliente?.nombre : undefined);
    if (!crudo) return '';
    // Va dentro de un HTML que app-tabla e info-table pintan sin sanitizar, y
    // el nombre lo carga un usuario: escapado (XSS almacenado).
    const nombre = escaparHtml(crudo);

    if (cadena.length < 2) return `<span>${nombre}</span>`;

    return (
      `<span style="display: inline-flex; align-items: center; gap: 4px;">` +
      `<span>${nombre}</span>` +
      `<span class="material-icons" style="font-size: 14px; opacity: 0.5;">account_tree</span>` +
      `</span>`
    );
  }
}
